import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, Search, Package, Tags, Layers, Wallet, Grid3x3, PackageCheck } from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';
const PAULMARK_BRAND_ID = '8591f281-715d-4a80-83cf-59bfb59f6ef3';

const fmtInt = (n) => Number(n || 0).toLocaleString('tr-TR');
const fmtTL = (n) =>
  n == null ? '—' : `${Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺`;
const fmtPct = (n, d = 1) =>
  `%${(Number(n || 0) * 100).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d })}`;

// Kategori/aralık barları için basit renk paleti (marka renklerinden bağımsız).
const BAR_COLORS = [
  '#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#14b8a6', '#f43f5e', '#84cc16', '#eab308',
];

function StatTile({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 text-gray-500">
        <Icon size={16} />
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-gray-400">{sub}</div>}
    </div>
  );
}

// Yatay bar-list: { name, count, ...} dizisi + toplam
function BarList({ title, subtitle, rows, total, extra }) {
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0) || 1;
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-1 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {subtitle && <span className="text-xs text-gray-400">{subtitle}</span>}
      </div>
      <div className="mt-3 space-y-2.5">
        {rows.map((r, i) => (
          <div key={r.name} className="group">
            <div className="mb-1 flex items-center justify-between text-sm">
              <span className="truncate pr-2 text-gray-700" title={r.name}>{r.name}</span>
              <span className="shrink-0 tabular-nums text-gray-500">
                {fmtInt(r.count)}
                {total ? <span className="ml-1 text-xs text-gray-400">({fmtPct(r.count / total, 0)})</span> : null}
                {extra ? <span className="ml-2 text-xs text-gray-400">{extra(r)}</span> : null}
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${(r.count / max) * 100}%`, backgroundColor: BAR_COLORS[i % BAR_COLORS.length] }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PaulmarkCatalog() {
  const { selectedBrand } = useAuth();
  const isPaulmark = selectedBrand?.id === PAULMARK_BRAND_ID;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');

  const fetchData = useCallback(async () => {
    if (!isPaulmark) return;
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_BASE}/api/paulmark-catalog/overview`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      setData(await res.json());
    } catch (e) {
      setData(null);
      setError(e?.message || 'Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [isPaulmark, selectedBrand?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const products = data?.products || [];
  const filtered = useMemo(() => {
    let list = products;
    if (segmentFilter) list = list.filter((p) => p.segment === segmentFilter);
    if (catFilter) list = list.filter((p) => p.category === catFilter);
    if (stockFilter) list = list.filter((p) => (stockFilter === 'in' ? p.inStock : !p.inStock));
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter(
        (p) => p.name.toLowerCase().includes(needle) || (p.sku || '').toLowerCase().includes(needle)
      );
    }
    return list;
  }, [products, q, segmentFilter, catFilter, stockFilter]);

  // ── Gate ──────────────────────────────────────────────────────────────────
  if (!selectedBrand?.id) {
    return <div className="p-6 text-center text-gray-500">Lütfen bir marka seçin.</div>;
  }
  if (!isPaulmark) {
    return (
      <div className="p-6">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-10 text-center">
          <p className="text-amber-800">Bu sayfa yalnızca <strong>Paulmark</strong> markası için kullanılabilir.</p>
        </div>
      </div>
    );
  }

  const t = data?.totals;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Paulmark · Ürün Kataloğu</h1>
          <p className="mt-1 text-sm text-gray-500">
            paulmark.com.tr kataloğu · segment, ürün tipi, stok ve fiyat aralığı dağılımı
            {data?.generatedAt && (
              <span className="ml-2 text-gray-400">
                · {new Date(data.generatedAt).toLocaleString('tr-TR')}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Yenile
        </button>
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <RefreshCw size={20} className="mr-2 animate-spin" /> Katalog yükleniyor…
        </div>
      )}

      {t && (
        <>
          {/* KPI tiles */}
          <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-5">
            <StatTile
              icon={Package}
              label="Ürün Sayfası"
              value={fmtInt(t.products)}
              sub={`${fmtInt(t.distinctNames)} farklı ürün adı`}
            />
            <StatTile
              icon={PackageCheck}
              label="Stokta"
              value={fmtInt(t.inStock)}
              sub={`${fmtInt(t.outOfStock)} tükendi · ${fmtPct(t.outOfStock / (t.products || 1), 0)}`}
            />
            <StatTile icon={Grid3x3} label="Ana Kategori" value={fmtInt(t.categories)} sub={`${fmtInt(t.productTypes)} ürün tipi`} />
            <StatTile icon={Tags} label="Segment" value={fmtInt(t.segments)} sub="kadın / erkek / çocuk" />
            <StatTile
              icon={Wallet}
              label="Ortalama Fiyat"
              value={fmtTL(t.avgPrice)}
              sub={`medyan ${fmtTL(t.medianPrice)} · ${fmtTL(t.minPrice)}–${fmtTL(t.maxPrice)}`}
            />
          </div>

          {/* Ana kategori + Fiyat aralığı yan yana */}
          <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <BarList
              title="Ana Kategoriye Göre Dağılım"
              subtitle={`${fmtInt(t.categories)} kategori`}
              rows={data.categories || []}
              total={t.products}
              extra={(r) => (r.avgPrice ? `ort ${fmtTL(r.avgPrice)}` : '')}
            />
            <BarList
              title="Fiyat Aralığına Göre Ürün Sayısı"
              subtitle={`${fmtInt(t.priced)} fiyatlı ürün`}
              rows={(data.priceBuckets || []).map((b) => ({ name: b.label, count: b.count }))}
              total={t.priced}
            />
          </div>

          {/* Segment + Ürün tipi yan yana */}
          <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <BarList
              title="Segmente Göre Ürün Sayısı"
              subtitle={`${fmtInt(t.segments)} segment`}
              rows={data.segments || []}
              total={t.products}
              extra={(r) => (r.avgPrice ? `ort ${fmtTL(r.avgPrice)}` : '')}
            />
            <BarList
              title="Ürün Tipine Göre Dağılım"
              subtitle={`${fmtInt(t.productTypes)} tip`}
              rows={(data.productTypes || []).slice(0, 20)}
              total={t.products}
              extra={(r) => (r.avgPrice ? `ort ${fmtTL(r.avgPrice)}` : '')}
            />
          </div>

          {/* Ürün tablosu */}
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-gray-900">
                Ürünler <span className="text-gray-400">({fmtInt(filtered.length)})</span>
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={catFilter}
                  onChange={(e) => setCatFilter(e.target.value)}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                >
                  <option value="">Tüm kategoriler</option>
                  {(data.categories || []).map((c) => (
                    <option key={c.name} value={c.name}>{c.name} ({c.count})</option>
                  ))}
                </select>
                <select
                  value={segmentFilter}
                  onChange={(e) => setSegmentFilter(e.target.value)}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                >
                  <option value="">Tüm segmentler</option>
                  {(data.segments || []).map((s) => (
                    <option key={s.name} value={s.name}>{s.name} ({s.count})</option>
                  ))}
                </select>
                <select
                  value={stockFilter}
                  onChange={(e) => setStockFilter(e.target.value)}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                >
                  <option value="">Tüm stok</option>
                  <option value="in">Stokta ({fmtInt(t.inStock)})</option>
                  <option value="out">Tükendi ({fmtInt(t.outOfStock)})</option>
                </select>
                <div className="relative">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Ürün adı / SKU ara…"
                    className="w-56 rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-700"
                  />
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wide text-gray-400">
                    <th className="py-2 pr-3 font-medium">Görsel</th>
                    <th className="py-2 pr-3 font-medium">Ürün</th>
                    <th className="py-2 pr-3 font-medium">Segment</th>
                    <th className="py-2 pr-3 font-medium">Kategori</th>
                    <th className="py-2 pr-3 font-medium">Tip</th>
                    <th className="py-2 pr-3 font-medium">SKU</th>
                    <th className="py-2 pr-3 font-medium">Stok</th>
                    <th className="py-2 pr-3 text-right font-medium">Fiyat</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 300).map((p, i) => (
                    <tr key={p.sku || i} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-2 pr-3">
                        {p.image ? (
                          <img
                            src={p.image}
                            alt=""
                            loading="lazy"
                            className="h-10 w-10 rounded-lg border border-gray-100 object-cover"
                            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-gray-100" />
                        )}
                      </td>
                      <td className="py-2 pr-3 max-w-xs">
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noreferrer"
                          className="line-clamp-2 text-gray-800 hover:text-indigo-600 hover:underline"
                          title={p.name}
                        >
                          {p.name}
                        </a>
                      </td>
                      <td className="py-2 pr-3 text-gray-600">{p.segment}</td>
                      <td className="py-2 pr-3 text-gray-500">{p.category}</td>
                      <td className="py-2 pr-3 text-gray-500">{p.productType}</td>
                      <td className="py-2 pr-3 font-mono text-xs text-gray-400">{p.sku}</td>
                      <td className="py-2 pr-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            p.inStock ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {p.inStock ? 'Stokta' : 'Tükendi'}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right font-medium tabular-nums text-gray-900">{fmtTL(p.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length > 300 && (
                <div className="mt-3 text-center text-xs text-gray-400">
                  İlk 300 ürün gösteriliyor · filtreyle daralt
                </div>
              )}
              {filtered.length === 0 && (
                <div className="py-10 text-center text-gray-400">Eşleşen ürün yok.</div>
              )}
            </div>

            <p className="mt-4 text-xs text-gray-400">
              Not: sitede her renk ayrı ürün sayfasıdır → {fmtInt(t.products)} satır,
              {' '}{fmtInt(t.distinctNames)} farklı ürün adına karşılık gelir. Beden ve indirim bilgisi
              site tarafında JS ile yüklendiği için katalogda yer almaz.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
